import { AppData, TechlogEntry, AircraftStats } from '../types';
import {
  loadFromSupabase,
  addTechlogEntryToSupabase,
  updateTechlogEntryInSupabase,
  clearAllSupabaseData,
} from './storageSupabase';

const STORAGE_KEY = 'crewStatusTrackerData';
const USE_SUPABASE = process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY;

// Debug logging
console.log('🔍 Storage Configuration:');
console.log('  REACT_APP_SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL ? '✓ Loaded' : '✗ Missing');
console.log('  REACT_APP_SUPABASE_ANON_KEY:', process.env.REACT_APP_SUPABASE_ANON_KEY ? '✓ Loaded' : '✗ Missing');
console.log('  USE_SUPABASE:', USE_SUPABASE ? '✅ Using Supabase' : '❌ Using localStorage');
console.log('');

// =====================================================
// LOCALSTORAGE FUNCTIONS (FALLBACK)
// =====================================================

const saveToLocalStorage = (data: AppData): void => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(STORAGE_KEY, serializedData);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const loadFromLocalStorageSync = (): AppData => {
  try {
    const serializedData = localStorage.getItem(STORAGE_KEY);
    if (serializedData) {
      const data = JSON.parse(serializedData);
      
      // Convert date strings back to Date objects
      const techlogEntries = data.techlogEntries?.map((entry: any) => ({
        ...entry,
        createdAt: new Date(entry.createdAt),
      })) || [];
      
      const aircraftStats = data.aircraftStats?.map((stat: any) => ({
        ...stat,
        lastUpdated: new Date(stat.lastUpdated),
      })) || [];
      
      return {
        techlogEntries,
        aircraftStats,
      };
    }
  } catch (error) {
    console.error('Error loading from localStorage:', error);
  }
  
  return {
    techlogEntries: [],
    aircraftStats: [],
  };
};

const addTechlogEntryToLocalStorage = (entry: TechlogEntry): void => {
  const data = loadFromLocalStorageSync();
  data.techlogEntries.push(entry);
  saveToLocalStorage(data);
};

const clearLocalStorageData = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

// =====================================================
// UNIFIED API (AUTO-SWITCHES BETWEEN SUPABASE AND LOCALSTORAGE)
// =====================================================

export const loadFromLocalStorage = (): AppData => {
  // For now, keep synchronous for backward compatibility
  // Components will be updated to use async version
  return loadFromLocalStorageSync();
};

export const loadData = async (): Promise<AppData> => {
  if (USE_SUPABASE) {
    try {
      return await loadFromSupabase();
    } catch (error) {
      console.error('Error loading from Supabase, falling back to localStorage:', error);
      return loadFromLocalStorageSync();
    }
  }
  return loadFromLocalStorageSync();
};

export const addTechlogEntry = async (entry: TechlogEntry): Promise<void> => {
  if (USE_SUPABASE) {
    try {
      await addTechlogEntryToSupabase(entry);
      return;
    } catch (error) {
      console.error('Error adding to Supabase, falling back to localStorage:', error);
    }
  }
  addTechlogEntryToLocalStorage(entry);
};

export const updateTechlogEntry = async (entry: TechlogEntry): Promise<void> => {
  if (USE_SUPABASE) {
    try {
      await updateTechlogEntryInSupabase(entry);
      return;
    } catch (error) {
      console.error('Error updating in Supabase, falling back to localStorage:', error);
    }
  }
  // For localStorage, find and update the entry
  const data = loadFromLocalStorageSync();
  const index = data.techlogEntries.findIndex(e => e.id === entry.id);
  if (index >= 0) {
    data.techlogEntries[index] = entry;
    saveToLocalStorage(data);
  }
};

export const updateAircraftStats = (stats: AircraftStats): void => {
  // Aircraft stats are now auto-calculated in Supabase via triggers
  // Keep this for localStorage fallback
  if (!USE_SUPABASE) {
    const data = loadFromLocalStorageSync();
    const existingIndex = data.aircraftStats.findIndex(
      s => s.registration === stats.registration
    );
    
    if (existingIndex >= 0) {
      data.aircraftStats[existingIndex] = stats;
    } else {
      data.aircraftStats.push(stats);
    }
    
    saveToLocalStorage(data);
  }
};

export const clearAllData = async (): Promise<void> => {
  if (USE_SUPABASE) {
    try {
      await clearAllSupabaseData();
      return;
    } catch (error) {
      console.error('Error clearing Supabase data:', error);
      throw error;
    }
  }
  clearLocalStorageData();
};
